import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, CircleDollarSign, AlertTriangle, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { qboTransactionLink } from "@/lib/qbo";
import { fmtDate } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const ageDays = (d) => (d ? Math.floor((Date.now() - new Date(d + "T00:00:00").getTime()) / 86400000) : null);

const METHOD_LABELS = {
  corpay_card: "Pay with Corpay card",
  corpay_billpay_check: "Corpay bill pay — check",
  corpay_billpay_ach: "Corpay bill pay — ACH",
  ramp: "Pay with Ramp Bill Pay",
};

const RAMP_STATUS = {
  APPROVAL_PENDING: "waiting for approval in Ramp", AWAITING_PAYMENT: "approved — payment scheduled",
  AWAITING_RELEASE: "waiting for release in Ramp", PAYMENT_PROCESSING: "payment in progress",
  PAYMENT_COMPLETED: "paid", PAYMENT_DETAILS_MISSING: "Ramp needs the vendor's payment details",
  PAYMENT_ERROR: "payment failed in Ramp", ON_HOLD: "on hold in Ramp", APPROVAL_REJECTED: "rejected in Ramp",
};
const rampStatus = (b) => RAMP_STATUS[b?.status_summary] || (b?.status_summary || "sent").toLowerCase().replace(/_/g, " ");

/** Accounts-payable view (Package 34), embedded as a tab inside the
 * Invoices page — the meeting feedback: don't pack the nav bar; unpaid
 * lives under Invoices. `initialTab` is "open" or "paid". */
export default function APInvoicesView({ initialTab = "open" }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [paying, setPaying] = useState(null); // invoice being paid
  const [manual, setManual] = useState({ method: "check", date: "", reference: "" });
  const [busy, setBusy] = useState(false);
  const [qboEnvironment, setQboEnvironment] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/contractor/invoices-ap").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // Needed only to build "View in QuickBooks" links on synced rows below;
    // a non-admin viewer without access to the QBO status endpoint just
    // won't get the link (pill still shows, plain text).
    api.get("/contractor/accounting/qbo-environment").then((r) => setQboEnvironment(r.data.environment)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byVendor = {};
    for (const inv of data.open) {
      (byVendor[inv.vendor_name || "Unknown vendor"] ||= []).push(inv);
    }
    return Object.entries(byVendor).map(([vendor, invoices]) => ({
      vendor, invoices,
      total: invoices.reduce((s, i) => s + (i.total || 0), 0),
      overdue: invoices.some((i) => i.overdue),
    })).sort((a, b) => (b.overdue - a.overdue) || (b.total - a.total));
  }, [data]);

  const platformMethods = Object.entries(data?.methods || {}).filter(([, on]) => on).map(([m]) => m);

  const payPlatform = async (method) => {
    setBusy(true);
    try {
      const r = await api.post(`/contractor/invoices/${paying.id}/pay`, { method });
      if (r.data.ok && r.data.pending) {
        toast.success(`Sent to Ramp Bill Pay. It's marked paid here once Ramp pays the vendor.`, { duration: 7000 });
        setPaying(null);
        load();
      } else if (r.data.ok) {
        const provider = method.startsWith("ramp") ? "Ramp" : "Corpay";
        toast.success(
          `${provider} accepted the payment — ref ${r.data.reference}${r.data.simulated ? " (sandbox)" : ""}. Invoice marked paid.`,
          { duration: 6000 },
        );
        setPaying(null);
        load();
      } else {
        // A disabled/unactivated provider is an outcome the person must
        // SEE, not a toast that blinks away.
        toast.error(r.data.message, { duration: 8000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (method, reference, date) => {
    setBusy(true);
    try {
      await api.post(`/contractor/invoices/${paying.id}/mark-paid`, {
        method, reference: reference || null, date: date || null,
      });
      const { data } = await api.get(`/contractor/invoices/${paying.id}`);
      toast.success(`Invoice ${paying.invoice_number} marked paid${data.qbo_payment_pending ? " — the payment goes to QuickBooks when you sync" : ""}`);
      setPaying(null);
      setManual({ method: "check", date: "", reference: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const rampAction = async (inv, action) => {
    setBusy(true);
    try {
      const { data: updated } = await api.post(`/contractor/invoices/${inv.id}/ramp-bill/${action}`);
      if (action === "cancel") toast.success("Ramp bill cancelled — you can pay it another way now");
      else toast.success(updated.status === "paid" ? "Ramp paid it — marked paid" : `Ramp: ${rampStatus(updated.ramp_bill)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ramp didn't respond");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { setTab(initialTab); }, [initialTab]);

  if (!data) return <div className="py-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div data-testid="open-invoices-view">
      <p className="text-slate-500 text-[12.5px] mt-1">Approved vendor invoices by due date. Payments happen on your own payment platform or the vendor's — ScapeSource records them and syncs the status to QuickBooks.</p>

      {tab === "open" && (
        <div className="mt-5 space-y-5">
          {grouped.map((g) => (
            <div key={g.vendor} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`ap-vendor-${g.vendor.replace(/[^a-zA-Z0-9]/g, "_")}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold text-[14px]">{g.vendor}</div>
                <div className="mono text-[13px] font-semibold">{fmt(g.total)}</div>
              </div>
              <table className="w-full mt-2 text-[13px]">
                <tbody>
                  {g.invoices.map((inv) => (
                    <tr key={inv.id} className={`border-t border-slate-50 ${inv.overdue ? "bg-red-50/50" : ""}`} data-testid={`ap-row-${inv.invoice_number}`}>
                      <td className="py-2.5">
                        <button onClick={() => navigate(`/contractor/invoices/${inv.id}`)} className="mono text-sky-600 font-medium hover:underline">{inv.invoice_number}</button>
                        <span className="ml-2 text-slate-400 mono text-[12px]">{inv.po_number}</span>
                      </td>
                      <td className="py-2.5 text-[12.5px]">
                        {inv.overdue ? (
                          <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><AlertTriangle size={13} /> Overdue — due {fmtDate(inv.due_date)} ({ageDays(inv.due_date)}d)</span>
                        ) : (
                          <span className="text-slate-500">Due {fmtDate(inv.due_date)}</span>
                        )}
                      </td>
                      <td className="py-2.5"><StatusBadge status={inv.accounting_status || inv.status} /></td>
                      <td className="py-2.5 text-right mono font-medium">{fmt(inv.total)}</td>
                      <td className="py-2.5 text-right">
                        {inv.ramp_bill?.id ? (
                          <div className="inline-flex flex-col items-end gap-1" data-testid={`ramp-bill-${inv.invoice_number}`}>
                            <span className={`text-[12px] font-semibold ${["PAYMENT_ERROR", "APPROVAL_REJECTED", "PAYMENT_DETAILS_MISSING"].includes(inv.ramp_bill.status_summary) ? "text-red-600" : "text-sky-700"}`}>
                              Ramp: {rampStatus(inv.ramp_bill)}
                            </span>
                            <span className="flex gap-2 text-[11.5px]">
                              <button disabled={busy} onClick={() => rampAction(inv, "refresh")} data-testid={`ramp-refresh-${inv.invoice_number}`} className="inline-flex items-center gap-1 text-sky-600 font-semibold hover:underline"><RefreshCw size={11} /> Check Ramp</button>
                              <button disabled={busy} onClick={() => rampAction(inv, "cancel")} data-testid={`ramp-cancel-${inv.invoice_number}`} className="text-slate-500 font-semibold hover:underline">Cancel Ramp bill</button>
                            </span>
                          </div>
                        ) : (
                          <button onClick={() => setPaying(inv)} data-testid={`pay-button-${inv.invoice_number}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[12px] font-semibold hover:bg-emerald-600">
                            <CircleDollarSign size={13} /> Pay / record
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {grouped.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No open invoices — everything approved is paid.</div>}
        </div>
      )}

      {tab === "paid" && (
        <div className="overflow-x-auto">
          <table className="w-full mt-5 text-[13px]" data-testid="ap-paid-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left py-2.5">Invoice #</th><th className="text-left py-2.5">Vendor</th><th className="text-left py-2.5">Paid</th><th className="text-left py-2.5">Method</th><th className="text-left py-2.5">Reference</th><th className="text-left py-2.5">QuickBooks</th><th className="text-right py-2.5">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.paid.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-50" data-testid={`ap-paid-row-${inv.invoice_number}`}>
                  <td className="py-2.5"><button onClick={() => navigate(`/contractor/invoices/${inv.id}`)} className="mono text-sky-600 font-medium hover:underline">{inv.invoice_number}</button></td>
                  <td className="py-2.5 text-slate-600">{inv.vendor_name}</td>
                  <td className="py-2.5 text-slate-600">{fmtDate(inv.paid_at)}</td>
                  <td className="py-2.5 text-slate-600">{inv.paid_method === "ramp_billpay" ? "Ramp Bill Pay" : (inv.paid_method || "—").replace(/_/g, " ")}</td>
                  <td className="py-2.5 mono text-slate-600">{inv.paid_reference || "—"}</td>
                  <td className="py-2.5">
                    {(() => {
                      const link = !qboEnvironment ? null
                        : inv.qbo_payment_ref ? qboTransactionLink(qboEnvironment, "BillPayment", inv.qbo_payment_ref)
                        : inv.qbo_sync ? qboTransactionLink(qboEnvironment, inv.qbo_sync.qbo_type, inv.qbo_sync.qbo_id)
                        : null;
                      const pill = inv.qbo_payment_ref
                        ? <span className="ss-pill ss-pill-green">Payment synced</span>
                        : inv.qbo_payment_pending ? <span className="ss-pill ss-pill-amber" data-testid={`payment-pending-${inv.invoice_number}`}>Payment not synced yet</span>
                        : inv.qbo_sync ? <span className="ss-pill ss-pill-slate">Bill synced</span> : <span className="ss-pill ss-pill-gray">Local</span>;
                      return link ? (
                        <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`view-in-qbo-${inv.invoice_number}`} className="inline-flex items-center gap-1 hover:opacity-80">
                          {pill} <ExternalLink size={10} className="text-slate-400" />
                        </a>
                      ) : pill;
                    })()}
                  </td>
                  <td className="py-2.5 text-right mono font-medium">{fmt(inv.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!paying} onOpenChange={(open) => !open && setPaying(null)}>
        <DialogContent data-testid="payment-modal">
          {paying && (
            <>
              <DialogHeader><DialogTitle>Pay {paying.invoice_number} — {fmt(paying.total)}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-[13px]">
                {paying.pay_link_url && (
                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="font-semibold">Vendor's payment link</div>
                    <a href={paying.pay_link_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-sky-600 font-medium hover:underline text-[12.5px]">
                      {new URL(paying.pay_link_url).hostname} <ExternalLink size={12} />
                    </a>
                    <div className="mt-1 text-[11.5px] text-slate-400">Opens the vendor's own merchant page. After paying there, confirm below.</div>
                    <button onClick={() => markPaid("vendor_link", null, null)} disabled={busy} data-testid="confirm-vendor-link-paid" className="mt-2 px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-50">
                      I paid via the vendor's link
                    </button>
                  </div>
                )}

                {platformMethods.length > 0 && (
                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="font-semibold">Your payment platform</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {platformMethods.map((m) => (
                        <button key={m} onClick={() => payPlatform(m)} disabled={busy} data-testid={`platform-pay-${m}`} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-700">
                          {METHOD_LABELS[m] || m}
                        </button>
                      ))}
                    </div>
                    {platformMethods.includes("ramp") && (
                      <div className="mt-2 text-[11.5px] text-slate-400">
                        Ramp Bill Pay creates this bill in Ramp. Ramp approves it and pays the vendor the way they're set up in Ramp. It's marked paid here once Ramp pays.
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="font-semibold">Record a manual payment</div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <SearchableSelect value={manual.method} onChange={(e) => setManual({ ...manual, method: e.target.value })} data-testid="manual-method-select" className="h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]">
                      <option value="check">Check</option>
                      <option value="credit_card">Credit card</option>
                    </SearchableSelect>
                    <input type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} data-testid="manual-date-input" className="h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]" />
                    <input value={manual.reference} onChange={(e) => setManual({ ...manual, reference: e.target.value })} placeholder="Check # / ref" data-testid="manual-reference-input" className="h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]" />
                  </div>
                  <button onClick={() => markPaid(manual.method, manual.reference, manual.date)} disabled={busy} data-testid="confirm-manual-paid" className="mt-2 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[12px] font-semibold hover:bg-emerald-600">
                    Mark paid
                  </button>
                </div>
                <div className="text-[11.5px] text-slate-400">ScapeSource never holds card numbers or bank details — money moves on your platform or the vendor's; this records it and updates QuickBooks.</div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
